/**
 * Custom hook for BCC City Selection state management
 */

import { useState, useRef, useMemo, useCallback } from 'react';
import {
    BCCCitySelection,
    BCCSelectionType,
    BCCSelectionLevel,
    BCCSelectionMode,
    IBCCCityList,
    ICitySelectionDropdownProps,
} from '@ts/common-types';
import {
    getCityId,
    buildCityDataStructure,
    getAllRegionsFromData,
    getAllTiersFromData,
    getAllZoneNames,
    getCitiesByZone,
    expandZoneToCityIds,
    expandRegionToCityIds,
    expandTierToCityIds,
    parseSelectionsFromValue,
    serializeSelectionsToValue,
    createCitySelection,
    createRegionSelection,
    createTierSelection,
    createZoneSelection,
    createCohortSelection,
    isCitySelected,
    isCityInCohort,
    isRegionSelected,
    isRegionInCohort,
    isTierSelected,
    isTierInCohort,
    isZoneSelected,
    isZoneInCohort,
    isCityInZoneSelection,
    isInAnyCohort,
    filterCitiesBySearch,
    filterStringsBySearch,
    generateCohortLabel,
    expandCohortItemsToCityIds,
} from '@ts/bcc-city-selection-utils';

// Re-export for convenience - uses the same props as the dropdown component
export type UseCitySelectionStateProps = ICitySelectionDropdownProps;

export interface UseCitySelectionStateReturn {
    // UI state
    ui: {
        isOpen: boolean;
        mode: BCCSelectionMode;
        selectedLevel: BCCSelectionLevel;
        individualLevel: BCCSelectionLevel;
        selectedCustomZone: string | null;
        searchQuery: string;
    };

    // Core state
    state: {
        selections: BCCCitySelection[];
        cohortBuilder: string[];
    };

    // Setters
    setters: {
        setMode: (mode: BCCSelectionMode) => void;
        setSelectedLevel: (level: BCCSelectionLevel) => void;
        setIndividualLevel: (level: BCCSelectionLevel) => void;
        setCohortBuilder: (items: string[]) => void;
        setSearchQuery: (query: string) => void;
        setSelectedCustomZone: (zone: string | null) => void;
    };

    // Handlers
    handlers: {
        handleDropdownOpen: () => void;
        handleDropdownClose: (isApplying: boolean) => void;
        clearAll: () => void;
        removeSelection: (id: number) => void;
    };

    // Individual selection toggles
    individual: {
        toggleCitySelection: (city: IBCCCityList) => void;
        toggleRegionSelection: (regionName: string) => void;
        toggleTierSelection: (tierName: string) => void;
        toggleZoneSelection: (zoneName: string) => void;
        toggleCustomZoneCitySelection: (city: IBCCCityList) => void;
    };

    // Cohort operations
    cohort: {
        toggleItem: (item: string) => void;
        create: () => void;
        getItems: () => Array<{ id: string; label: string }>;
    };

    // Select all operations
    selectAll: {
        toggleCities: () => void;
        toggleRegions: () => void;
        toggleTiers: () => void;
        toggleCohortItems: () => void;
    };

    // Check functions
    checks: {
        citySelected: (cityId: string) => boolean;
        cityInCohort: (cityId: string) => boolean;
        regionSelected: (regionName: string) => boolean;
        regionInCohort: (regionName: string) => boolean;
        tierSelected: (tierName: string) => boolean;
        tierInCohort: (tierName: string) => boolean;
        zoneSelected: (zoneName: string) => boolean;
        zoneInCohort: (zoneName: string) => boolean;
        cityInZoneSelection: (cityId: string) => boolean;
        inAnyCohort: (itemId: string, level: BCCSelectionLevel) => boolean;
    };

    // Filtered data getters
    filters: {
        getCities: () => IBCCCityList[];
        getRegions: () => string[];
        getTiers: () => string[];
        getZones: () => string[];
        getCitiesInZone: (zoneName: string) => IBCCCityList[];
    };

    // Selectable items getters
    selectors: {
        getCities: () => IBCCCityList[];
        getRegions: () => string[];
        getTiers: () => string[];
        getCohortItems: () => Array<{ id: string; label: string }>;
    };

    // All selected checks
    allSelected: {
        cities: () => boolean;
        regions: () => boolean;
        tiers: () => boolean;
        cohortItems: () => boolean;
    };

    // Utility functions
    utils: {
        getCityIdFn: (city: IBCCCityList) => string;
    };
}

export const useCitySelectionState = ({
    value,
    citiesList,
    record,
    handleFieldChange,
}: UseCitySelectionStateProps): UseCitySelectionStateReturn => {
    // Core state
    const [isOpen, setIsOpen] = useState(false);
    const [mode, setMode] = useState<BCCSelectionMode>(BCCSelectionMode.INDIVIDUAL);
    const [selectedLevel, setSelectedLevel] = useState<BCCSelectionLevel>(BCCSelectionLevel.CITY);
    const [individualLevel, setIndividualLevel] = useState<BCCSelectionLevel>(BCCSelectionLevel.CITY);
    const [cohortBuilder, setCohortBuilder] = useState<string[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCustomZone, setSelectedCustomZone] = useState<string | null>(null);

    // Refs for revert functionality
    const savedSelectionsRef = useRef<BCCCitySelection[]>([]);

    // Build city data structure
    const cityData = useMemo(() => buildCityDataStructure(citiesList), [citiesList]);

    // Derived data
    const allCities = citiesList;
    const allRegions = getAllRegionsFromData(cityData);
    const allTiers = getAllTiersFromData(cityData);
    const allZones = getAllZoneNames();

    // Initialize selections from value
    const [selections, setSelections] = useState<BCCCitySelection[]>(() =>
        parseSelectionsFromValue(value, citiesList)
    );

    // Update parent with new selections
    const updateParent = useCallback(
        (newSelections: BCCCitySelection[]) => {
            const cityIds = serializeSelectionsToValue(newSelections);
            handleFieldChange(record.id || '', BCCSelectionLevel.CITY, cityIds, record);
        },
        [handleFieldChange, record]
    );

    // Dropdown open/close handlers
    const handleDropdownOpen = useCallback(() => {
        savedSelectionsRef.current = JSON.parse(JSON.stringify(selections));
        setIsOpen(true);
    }, [selections]);

    const handleDropdownClose = useCallback(
        (isApplying: boolean) => {
            if (!isApplying) {
                setSelections(savedSelectionsRef.current);
                updateParent(savedSelectionsRef.current);
            }
            setIsOpen(false);
        },
        [updateParent]
    );

    // Toggle cohort item
    const toggleCohortItem = useCallback((item: string) => {
        setCohortBuilder((prev) =>
            prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]
        );
    }, []);

    // Create cohort from builder
    const createCohort = useCallback(() => {
        if (cohortBuilder.length >= 2) {
            const label = generateCohortLabel(cohortBuilder, selectedLevel, citiesList);
            const expandedCityIds = expandCohortItemsToCityIds(
                cohortBuilder,
                selectedLevel,
                cityData,
                citiesList
            );

            const newSelection = createCohortSelection(
                selectedLevel,
                cohortBuilder,
                expandedCityIds,
                label
            );

            const newSelections = [...selections, newSelection];
            setSelections(newSelections);
            updateParent(newSelections);
            setCohortBuilder([]);
        }
    }, [cohortBuilder, selectedLevel, citiesList, cityData, selections, updateParent]);

    // Remove selection
    const removeSelection = useCallback(
        (id: number) => {
            const newSelections = selections.filter((s) => s.id !== id);
            setSelections(newSelections);
            updateParent(newSelections);
        },
        [selections, updateParent]
    );

    // Clear all selections
    const clearAll = () => {
        setSelections([]);
        updateParent([]);
    };

    // Individual toggle functions
    const toggleIndividualCitySelection = useCallback(
        (city: IBCCCityList) => {
            const cityId = getCityId(city);
            const existing = selections.find(
                (s) => s.type === BCCSelectionType.CITY && s.items.includes(cityId)
            );

            const newSelections = existing
                ? selections.filter((s) => s.id !== existing.id)
                : [...selections, createCitySelection(city)];

            setSelections(newSelections);
            updateParent(newSelections);
        },
        [selections, updateParent]
    );

    const toggleIndividualRegionSelection = useCallback(
        (regionName: string) => {
            const existing = selections.find(
                (s) => s.type === BCCSelectionType.REGION && s.label === regionName
            );

            let newSelections: BCCCitySelection[];
            if (existing) {
                newSelections = selections.filter((s) => s.id !== existing.id);
            } else {
                const regionCityIds = expandRegionToCityIds(cityData, regionName);
                if (regionCityIds.length === 0) return;
                newSelections = [
                    ...selections,
                    createRegionSelection(regionName, regionCityIds),
                ];
            }

            setSelections(newSelections);
            updateParent(newSelections);
        },
        [selections, cityData, updateParent]
    );

    const toggleIndividualTierSelection = useCallback(
        (tierName: string) => {
            const existing = selections.find(
                (s) => s.type === BCCSelectionType.TIER && s.label === tierName
            );

            let newSelections: BCCCitySelection[];
            if (existing) {
                newSelections = selections.filter((s) => s.id !== existing.id);
            } else {
                const tierCityIds = expandTierToCityIds(cityData, tierName);
                if (tierCityIds.length === 0) return;
                newSelections = [
                    ...selections,
                    createTierSelection(tierName, tierCityIds),
                ];
            }

            setSelections(newSelections);
            updateParent(newSelections);
        },
        [selections, cityData, updateParent]
    );

    const toggleIndividualZoneSelection = useCallback(
        (zoneName: string) => {
            const existing = selections.find(
                (s) => s.type === BCCSelectionType.ZONE && s.label === zoneName
            );

            let newSelections: BCCCitySelection[];
            if (existing) {
                newSelections = selections.filter((s) => s.id !== existing.id);
            } else {
                const zoneCityIds = expandZoneToCityIds(citiesList, zoneName);
                if (zoneCityIds.length === 0) return;
                newSelections = [
                    ...selections,
                    createZoneSelection(zoneName, zoneCityIds),
                ];
            }

            setSelections(newSelections);
            updateParent(newSelections);
        },
        [selections, citiesList, updateParent]
    );

    const toggleCustomZoneCitySelection = useCallback(
        (city: IBCCCityList) => {
            const cityId = getCityId(city);
            const existing = selections.find(
                (s) => s.type === BCCSelectionType.CITY && s.items.includes(cityId)
            );

            const newSelections = existing
                ? selections.filter((s) => s.id !== existing.id)
                : [...selections, createCitySelection(city)];

            setSelections(newSelections);
            updateParent(newSelections);
        },
        [selections, updateParent]
    );

    // Check functions grouped in a single memo to avoid recreating many tiny closures
    const checks = useMemo(
        () => ({
            citySelected: (cityId: string) => isCitySelected(selections, cityId),
            cityInCohort: (cityId: string) => isCityInCohort(selections, cityId),
            regionSelected: (regionName: string) => isRegionSelected(selections, regionName),
            regionInCohort: (regionName: string) => isRegionInCohort(selections, regionName),
            tierSelected: (tierName: string) => isTierSelected(selections, tierName),
            tierInCohort: (tierName: string) => isTierInCohort(selections, tierName),
            zoneSelected: (zoneName: string) => isZoneSelected(selections, zoneName),
            zoneInCohort: (zoneName: string) => isZoneInCohort(selections, zoneName),
            cityInZoneSelection: (cityId: string) => isCityInZoneSelection(selections, cityId),
            inAnyCohort: (itemId: string, level: BCCSelectionLevel) =>
                isInAnyCohort(selections, itemId, level),
        }),
        [selections]
    );

    // Filtered data getters grouped via memo
    const filters = useMemo(
        () => ({
            getCities: () => filterCitiesBySearch(allCities, searchQuery),
            getRegions: () => filterStringsBySearch(allRegions, searchQuery),
            getTiers: () => filterStringsBySearch(allTiers, searchQuery),
            getZones: () => filterStringsBySearch(allZones, searchQuery),
            getCitiesInZone: (zoneName: string) =>
                filterCitiesBySearch(getCitiesByZone(citiesList, zoneName), searchQuery),
        }),
        [allCities, allRegions, allTiers, allZones, citiesList, searchQuery]
    );

    // Get cohort items based on selected level
    const getCohortItems = useCallback((): Array<{ id: string; label: string }> => {
        switch (selectedLevel) {
            case BCCSelectionLevel.CITY:
                return allCities.map((city) => ({
                    id: getCityId(city),
                    label: city.city_name,
                }));
            case BCCSelectionLevel.REGION:
                return allRegions.map((region) => ({
                    id: region,
                    label: region,
                }));
            case BCCSelectionLevel.TIER:
                return allTiers.map((tier) => ({
                    id: tier,
                    label: tier,
                }));
            case BCCSelectionLevel.CUSTOM:
                return allZones.map((zone) => ({
                    id: zone,
                    label: zone,
                }));
            default:
                return [];
        }
    }, [selectedLevel, allCities, allRegions, allTiers, allZones]);

    // Selectable items getters (excluding items already in cohorts)
    const getSelectableCities = useCallback(
        () => filters.getCities().filter((city) => !checks.cityInCohort(getCityId(city))),
        [filters, checks]
    );

    const getSelectableRegions = useCallback(
        () => filters.getRegions().filter((region) => !checks.regionInCohort(region)),
        [filters, checks]
    );

    const getSelectableTiers = useCallback(
        () => filters.getTiers().filter((tier) => !checks.tierInCohort(tier)),
        [filters, checks]
    );

    const getSelectableCohortItems = useCallback(() => {
        return getCohortItems().filter((item) => {
            let isIndividuallySelected = false;
            switch (selectedLevel) {
                case BCCSelectionLevel.CITY:
                    isIndividuallySelected = checks.citySelected(item.id);
                    break;
                case BCCSelectionLevel.REGION:
                    isIndividuallySelected = checks.regionSelected(item.id);
                    break;
                case BCCSelectionLevel.TIER:
                    isIndividuallySelected = checks.tierSelected(item.id);
                    break;
                case BCCSelectionLevel.CUSTOM:
                    isIndividuallySelected = checks.zoneSelected(item.id);
                    break;
            }
            const isAlreadyInCohort = checks.inAnyCohort(item.id, selectedLevel);
            return !isIndividuallySelected && !isAlreadyInCohort;
        });
    }, [
        getCohortItems,
        selectedLevel,
        checks,
    ]);

    // All selected checks
    const areAllCitiesSelected = useMemo(() => {
        const selectableCities = getSelectableCities();
        if (selectableCities.length === 0) return false;
        return selectableCities.every((city) =>
            checks.citySelected(getCityId(city))
        );
    }, [getSelectableCities, checks]);

    const areAllRegionsSelected = useMemo(() => {
        const selectableRegions = getSelectableRegions();
        if (selectableRegions.length === 0) return false;
        return selectableRegions.every((region) =>
            checks.regionSelected(region)
        );
    }, [getSelectableRegions, checks]);

    const areAllTiersSelected = useMemo(() => {
        const selectableTiers = getSelectableTiers();
        if (selectableTiers.length === 0) return false;
        return selectableTiers.every((tier) => checks.tierSelected(tier));
    }, [getSelectableTiers, checks]);

    const areAllCohortItemsSelected = useMemo(() => {
        const selectableItems = getSelectableCohortItems();
        if (selectableItems.length === 0) return false;
        return selectableItems.every((item) => cohortBuilder.includes(item.id));
    }, [getSelectableCohortItems, cohortBuilder]);
      

    // Generic helper for select-all toggles
    const runToggleSelectAll = useCallback(<T,>({
        selectableItems,
        allSelected,
        isItemSelected,
        createSelection,
        shouldRemoveSelection,
    }: {
        selectableItems: T[];
        allSelected: boolean;
        isItemSelected: (item: T) => boolean;
        createSelection: (item: T) => BCCCitySelection | null;
        shouldRemoveSelection: (selection: BCCCitySelection, selectableItems: T[]) => boolean;
    }) => {
        let nextSelections: BCCCitySelection[];

        if (allSelected) {
            nextSelections = selections.filter(
                (selection) => !shouldRemoveSelection(selection, selectableItems)
            );
        } else {
            const newSelections = selectableItems
                .filter((item) => !isItemSelected(item))
                .map(createSelection)
                .filter(Boolean) as BCCCitySelection[];
            nextSelections = [...selections, ...newSelections];
        }

        setSelections(nextSelections);
        updateParent(nextSelections);
    }, [selections, updateParent]);

    // Select All toggle functions
    const toggleSelectAllCities = useCallback(() => {
        const selectableCities = getSelectableCities();
        const selectableCityIds = selectableCities.map((city) => getCityId(city));

        runToggleSelectAll({
            selectableItems: selectableCities,
            allSelected: areAllCitiesSelected,
            isItemSelected: (city) => checks.citySelected(getCityId(city)),
            createSelection: (city) => createCitySelection(city),
            shouldRemoveSelection: (selection) =>
                selection.type === BCCSelectionType.CITY &&
                selectableCityIds.includes(selection.items[0]),
        });
    }, [getSelectableCities, areAllCitiesSelected, checks, runToggleSelectAll]);

    const toggleSelectAllRegions = useCallback(() => {
        const selectableRegions = getSelectableRegions();

        runToggleSelectAll({
            selectableItems: selectableRegions,
            allSelected: areAllRegionsSelected,
            isItemSelected: (region) => checks.regionSelected(region),
            createSelection: (region) => {
                const regionCityIds = expandRegionToCityIds(cityData, region);
                return regionCityIds.length > 0 ? createRegionSelection(region, regionCityIds) : null;
            },
            shouldRemoveSelection: (selection) =>
                selection.type === BCCSelectionType.REGION &&
                selectableRegions.includes(selection.label),
        });
    }, [getSelectableRegions, areAllRegionsSelected, checks, cityData, runToggleSelectAll]);

    const toggleSelectAllTiers = useCallback(() => {
        const selectableTiers = getSelectableTiers();

        runToggleSelectAll({
            selectableItems: selectableTiers,
            allSelected: areAllTiersSelected,
            isItemSelected: (tier) => checks.tierSelected(tier),
            createSelection: (tier) => {
                const tierCityIds = expandTierToCityIds(cityData, tier);
                return tierCityIds.length > 0 ? createTierSelection(tier, tierCityIds) : null;
            },
            shouldRemoveSelection: (selection) =>
                selection.type === BCCSelectionType.TIER && selectableTiers.includes(selection.label),
        });
    }, [getSelectableTiers, areAllTiersSelected, checks, cityData, runToggleSelectAll]);

    const toggleSelectAllCohortItems = useCallback(() => {
        setCohortBuilder((prev) => {
            const selectableItems = getSelectableCohortItems();
            if (selectableItems.length === 0) return prev;
            
            const selectableIds = selectableItems.map((item) => item.id);
            const allSelected = selectableItems.every((item) => prev.includes(item.id));

            if (allSelected) {
                return prev.filter((id) => !selectableIds.includes(id));
            }

            return [...new Set([...prev, ...selectableIds])];
        });
    }, [getSelectableCohortItems]);

    return {
        ui: {
            isOpen,
            mode,
            selectedLevel,
            individualLevel,
            selectedCustomZone,
            searchQuery,
        },
        state: {
            selections,
            cohortBuilder,
        },
        setters: {
            setMode,
            setSelectedLevel,
            setIndividualLevel,
            setCohortBuilder,
            setSearchQuery,
            setSelectedCustomZone,
        },
        handlers: {
            handleDropdownOpen,
            handleDropdownClose,
            clearAll,
            removeSelection,
        },
        individual: {
            toggleCitySelection: toggleIndividualCitySelection,
            toggleRegionSelection: toggleIndividualRegionSelection,
            toggleTierSelection: toggleIndividualTierSelection,
            toggleZoneSelection: toggleIndividualZoneSelection,
            toggleCustomZoneCitySelection,
        },
        cohort: {
            toggleItem: toggleCohortItem,
            create: createCohort,
            getItems: getCohortItems,
        },
        selectAll: {
            toggleCities: toggleSelectAllCities,
            toggleRegions: toggleSelectAllRegions,
            toggleTiers: toggleSelectAllTiers,
            toggleCohortItems: toggleSelectAllCohortItems,
        },
        checks: {
            citySelected: checks.citySelected,
            cityInCohort: checks.cityInCohort,
            regionSelected: checks.regionSelected,
            regionInCohort: checks.regionInCohort,
            tierSelected: checks.tierSelected,
            tierInCohort: checks.tierInCohort,
            zoneSelected: checks.zoneSelected,
            zoneInCohort: checks.zoneInCohort,
            cityInZoneSelection: checks.cityInZoneSelection,
            inAnyCohort: checks.inAnyCohort,
        },
        filters: {
            getCities: filters.getCities,
            getRegions: filters.getRegions,
            getTiers: filters.getTiers,
            getZones: filters.getZones,
            getCitiesInZone: filters.getCitiesInZone,
        },
        selectors: {
            getCities: getSelectableCities,
            getRegions: getSelectableRegions,
            getTiers: getSelectableTiers,
            getCohortItems: getSelectableCohortItems,
        },
        allSelected: {
            cities: () => areAllCitiesSelected,
            regions: () => areAllRegionsSelected,
            tiers: () => areAllTiersSelected,
            cohortItems: () => areAllCohortItemsSelected,
        },
        utils: {
            getCityIdFn: getCityId,
        },
    };
};

export default useCitySelectionState;

